import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider.jsx'
import { firstError } from '../utils/format.js'
import { BrandLockup } from '../components/Brand.jsx'
import { api } from '../api/client.js'

/**
 * Public patient self-registration — 3-step branded wizard.
 *
 *   1. Identity      — first / last / sex / DOB
 *   2. Contact + org — phone (PNG-format hint), place, email, org_invite_code
 *   3. Account       — username (optional), password + strength, consent
 *
 * Honeypot field `website` is hidden from real users; bots fill it in
 * and the backend rejects the POST.
 *
 * Org self-enrollment: if the user supplies a code matching an active
 * org's `self_enroll_code`, the backend creates an
 * OrganizationStaffLink in the same atomic transaction so the patient
 * lands in the right org's workforce-health view automatically.
 */
export default function Register() {
  const { signUpPatient, user } = useAuth()
  const nav = useNavigate()
  const [searchParams] = useSearchParams()

  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)  // {username, hadEmail} after register

  // Allow ?code=XYZ in the URL so HR teams can email a "click here to
  // self-enroll" link with the org code pre-filled.
  const initialCode = searchParams.get('code') || ''

  const [form, setForm] = useState({
    // Identity
    first_name: '', last_name: '', sex: 'male', date_of_birth: '',
    // Contact + org
    phone_number: '', place_of_residence: '', email: '',
    org_invite_code: initialCode,
    // Account
    username: '', password: '', confirm: '',
    // Consents
    terms_of_service: false,
    telehealth_treatment: false,
    data_sharing_with_employer: false,
    // Honeypot — bots will fill this; real users never see it.
    website: '',
  })

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.type === 'checkbox' ? e.target.checked : e.target.value }))

  // If somehow a signed-in user lands here, send them home.
  useEffect(() => {
    if (user) nav('/', { replace: true })
  }, [user, nav])

  // ---- Validation per step -----------------------------------------------
  function validateStep1() {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      return 'Please enter your first and last name.'
    }
    if (!['male', 'female', 'other'].includes(form.sex)) {
      return 'Please select your sex.'
    }
    return null
  }
  function validateStep2() {
    // PNG-context contact policy: at least ONE of email or phone must
    // be provided so the patient has a recovery channel. Mirrors the
    // backend rule in patients.validators.require_at_least_one_contact.
    const hasEmail = Boolean((form.email || '').trim())
    const hasPhone = Boolean((form.phone_number || '').trim())
    if (!hasEmail && !hasPhone) {
      return 'Please provide either an email OR a phone number so we can help you recover your account.'
    }
    if (form.phone_number && !/^[+\d][\d\s\-()]{6,}$/.test(form.phone_number)) {
      return 'Phone number looks invalid. Use a format like +675 7000 1234.'
    }
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      return 'Email format is not valid.'
    }
    if (form.org_invite_code && !/^[A-Za-z0-9-]{2,24}$/.test(form.org_invite_code.trim())) {
      return 'Organisation invitation code can contain only letters, numbers and dashes.'
    }
    return null
  }
  function validateStep3() {
    if (form.password.length < 8) return 'Password must be at least 8 characters.'
    if (form.password !== form.confirm) return 'Passwords do not match.'
    if (!form.terms_of_service) return 'Please accept the Terms of Service.'
    if (!form.telehealth_treatment) return 'Please consent to telehealth treatment.'
    return null
  }

  function next() {
    setErr(null)
    const v = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null
    if (v) { setErr(v); return }
    setStep((s) => Math.min(3, s + 1))
  }
  function back() { setErr(null); setStep((s) => Math.max(1, s - 1)) }

  async function onSubmit(e) {
    e.preventDefault()
    setErr(null)
    const v = validateStep3()
    if (v) { setErr(v); return }

    setBusy(true)
    try {
      // Build the payload. Strip blank optional fields. Translate the
      // checkbox booleans into the consents[] array the backend expects.
      const consents = []
      if (form.terms_of_service)         consents.push('terms_of_service')
      if (form.telehealth_treatment)     consents.push('telehealth_treatment')
      if (form.data_sharing_with_employer) consents.push('data_sharing_with_employer')

      const payload = {
        first_name:   form.first_name.trim(),
        last_name:    form.last_name.trim(),
        sex:          form.sex,
        password:     form.password,
        consents,
        // Honeypot — backend rejects if non-empty.
        website:      form.website,
      }
      // Optional fields: include only when populated.
      const optional = {
        username: form.username, email: form.email,
        date_of_birth: form.date_of_birth, phone_number: form.phone_number,
        place_of_residence: form.place_of_residence,
        org_invite_code: form.org_invite_code.trim(),
      }
      Object.entries(optional).forEach(([k, v]) => { if (v) payload[k] = v })

      const u = await signUpPatient(payload)
      // If the user left username blank, they probably never noticed
      // what got auto-generated. Show a confirmation step displaying it
      // before we send them on to /patient — so they have something
      // memorable to type next time they sign in.
      const hadExplicitUsername = Boolean(payload.username)
      const hadEmail = Boolean(payload.email)
      if (!hadExplicitUsername) {
        setSuccess({ username: u?.username, hadEmail })
        return
      }
      nav('/', { replace: true })  // RoleHome → /patient
    } catch (e) {
      setErr(firstError(e, 'Registration failed'))
    } finally {
      setBusy(false)
    }
  }

  // Once the user dismisses the "save your username" success card we send
  // them on to their patient home. Wired to the manual button so the
  // confirmation never auto-disappears before the user has read it.
  function continueToHome() {
    nav('/', { replace: true })
  }

  // ---- Render -------------------------------------------------------------
  // Success state — shown after registration when the user left username
  // blank. They probably never saw what got auto-generated; we display
  // it here so they can save it for next time. The user is *already*
  // signed in (the API returned JWTs) — this is purely informational.
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-wantok-50 via-cream to-emerald-50/40 flex flex-col">
        <header className="bg-wantok-gradient text-white">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <BrandLockup variant="light" linkTo="/login" size={36} />
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-card p-6">
            <div className="flex items-center gap-2 text-emerald-700">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-white text-base font-bold">✓</span>
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold">Account created</span>
            </div>
            <h1 className="text-2xl font-display font-bold text-slate-900 mt-2">Welcome to Wantok Telehealth</h1>
            <p className="text-sm text-slate-600 mt-1">
              You're signed in. Before you continue, save your sign-in details somewhere safe.
            </p>

            <div className="mt-4 rounded-lg bg-wantok-50 border border-wantok-100 p-4 space-y-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-wantok-700">Your username</div>
              <div className="font-mono text-lg text-slate-900 select-all">{success.username}</div>
              <div className="text-xs text-slate-600">
                We auto-generated this from your name because you left the username blank.
                {success.hadEmail
                  ? ' You can also sign in with your email.'
                  : ' Save it — you\'ll need it to sign back in.'}
              </div>
            </div>

            <div className="mt-4 text-xs text-slate-500">
              <strong className="text-slate-700">Forgot your password later?</strong>{' '}
              {success.hadEmail
                ? <>Use the <Link to="/forgot" className="text-wantok-700 underline">Forgot password</Link> link from the sign-in page — we'll email you a reset link.</>
                : <>You'll need to add an email to your profile so we can reset your password if you ever forget it.</>
              }
            </div>

            <button
              type="button" onClick={continueToHome}
              className="mt-6 w-full bg-wantok-700 hover:bg-wantok-800 text-white rounded-full px-5 py-2 text-sm font-semibold"
            >
              Continue to my account →
            </button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-wantok-50 via-cream to-coral-50/50 flex flex-col">
      {/* Branded header */}
      <header className="bg-wantok-gradient text-white shadow-soft relative">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px]"
             style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(20,184,166,0.6) 30%, rgba(238,77,31,0.7) 50%, rgba(20,184,166,0.6) 70%, transparent 100%)' }} />
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <BrandLockup variant="light" linkTo="/login" size={36} />
          <Link to="/login" className="text-xs uppercase tracking-[0.18em] font-bold text-white/80 hover:text-white">
            Sign in →
          </Link>
        </div>
      </header>

      {/* Hero / value prop */}
      <section className="max-w-3xl mx-auto w-full px-4 pt-6 pb-2">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-wantok-700">
          Create your patient account
        </div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">
          Join Wantok Telehealth
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          See your encounters, prescriptions, lab results and imaging reports in one place.
          Connect to your employer if they're an ICER customer — or sign up on your own.
        </p>
      </section>

      {/* Step indicator */}
      <section className="max-w-3xl mx-auto w-full px-4 pt-3 pb-2">
        <Stepper step={step} />
      </section>

      {/* Form card */}
      <main className="flex-1 px-4 pb-10">
        <form onSubmit={onSubmit}
              className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-card p-6 space-y-5">
          {step === 1 && <Step1 form={form} set={set} />}
          {step === 2 && <Step2 form={form} set={set} />}
          {step === 3 && <Step3 form={form} set={set} />}

          {/* Honeypot — visually hidden but present in the DOM. Bots
              that auto-fill every form field will populate this; real
              users never see it. */}
          <div aria-hidden="true" className="absolute left-[-10000px] top-auto w-px h-px overflow-hidden">
            <label>
              Website (do not fill)
              <input
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={set('website')}
              />
            </label>
          </div>

          {err && (
            <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
              {err}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <button type="button" onClick={back} disabled={step === 1}
                    className="text-sm text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
              ← Back
            </button>
            {step < 3 ? (
              <button type="button" onClick={next}
                      className="bg-wantok-700 hover:bg-wantok-800 text-white rounded-full px-5 py-2 text-sm font-semibold">
                Continue →
              </button>
            ) : (
              <button type="submit" disabled={busy}
                      className="bg-wantok-700 hover:bg-wantok-800 text-white rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-60">
                {busy ? 'Creating account…' : 'Create account'}
              </button>
            )}
          </div>

          <div className="text-center text-xs text-slate-500 pt-2">
            Already have an account?{' '}
            <Link to="/login" className="text-wantok-700 underline font-medium">Sign in</Link>
            {' · '}
            <Link to="/forgot" className="text-wantok-700 underline font-medium">Forgot password?</Link>
          </div>
        </form>

        <p className="text-[11px] text-slate-400 text-center mt-4 max-w-2xl mx-auto">
          Clinicians and staff are not self-registered — they join by invitation only.
          Your information is stored on PNG-resident infrastructure with k=5 privacy floor on aggregate analytics.
        </p>
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

function Step1({ form, set }) {
  return (
    <section className="space-y-4">
      <SectionHeader
        eyebrow="Step 1 of 3"
        title="Tell us who you are"
        sub="Required: a clinician seeing your record needs at least your name and sex."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="First name *">
          <Input value={form.first_name} onChange={set('first_name')} autoFocus autoComplete="given-name" />
        </Field>
        <Field label="Last name *">
          <Input value={form.last_name} onChange={set('last_name')} autoComplete="family-name" />
        </Field>
        <Field label="Sex *">
          <Select value={form.sex} onChange={set('sex')}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <Field label="Date of birth (optional)">
          <Input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} autoComplete="bday" />
        </Field>
      </div>
    </section>
  )
}

function Step2({ form, set }) {
  // Resolve the org name from the invite code (debounced) so the
  // patient sees a confirmation banner ("You're enrolling under: …")
  // before they hit submit. Falls quiet on bad codes — the actual
  // submit will surface a precise validation error.
  const code = (form.org_invite_code || '').trim()
  const [orgInfo, setOrgInfo] = useState(null)
  const [orgLookup, setOrgLookup] = useState('idle') // idle|loading|found|notfound
  useEffect(() => {
    if (!code || code.length < 3) {
      setOrgInfo(null); setOrgLookup('idle'); return
    }
    let cancelled = false
    setOrgLookup('loading')
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/api/auth/resolve-org-code/', {
          params: { code },
        })
        if (cancelled) return
        setOrgInfo(data); setOrgLookup('found')
      } catch {
        if (cancelled) return
        setOrgInfo(null); setOrgLookup('notfound')
      }
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
  }, [code])

  return (
    <section className="space-y-4">
      <SectionHeader
        eyebrow="Step 2 of 3"
        title="How can we reach you?"
        sub="At least ONE of email or phone is required so we can help you recover your account. The rest are optional."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Phone number" hint="PNG format: +675 followed by your number">
          <Input
            value={form.phone_number} onChange={set('phone_number')}
            placeholder="+675 7000 1234" autoComplete="tel" inputMode="tel"
          />
        </Field>
        <Field label="Place of residence">
          <Input
            value={form.place_of_residence} onChange={set('place_of_residence')}
            placeholder="Port Moresby, NCD"
          />
        </Field>
        <Field label="Email">
          <Input
            type="email" value={form.email} onChange={set('email')}
            placeholder="you@example.com" autoComplete="email"
          />
        </Field>
        <Field label="Organisation invitation code"
               hint="If your employer issued one, enter it here so your dashboard is linked.">
          <Input
            value={form.org_invite_code} onChange={set('org_invite_code')}
            placeholder="e.g. OPIC2026" autoComplete="off"
          />
        </Field>
      </div>

      {/* Org-name resolver banner — surfaces what the entered code
          actually maps to, so the patient sees "You're enrolling
          under: <Org>" before submitting. Hidden when the code is
          empty or still being typed. */}
      {code && code.length >= 3 && (
        <div className="text-xs">
          {orgLookup === 'loading' && (
            <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-500 italic">
              Checking organisation code…
            </div>
          )}
          {orgLookup === 'found' && orgInfo && (
            <div className="bg-wantok-50 border border-wantok-200 rounded px-3 py-2 text-wantok-800">
              <span className="font-semibold">You're enrolling under:</span>{' '}
              <span className="font-bold">{orgInfo.name}</span>
              {orgInfo.centre_name && (
                <>
                  <span className="text-wantok-700"> · </span>
                  <span className="font-bold">{orgInfo.centre_name}</span>
                  {orgInfo.centre_location && (
                    <span className="text-wantok-700"> ({orgInfo.centre_location})</span>
                  )}
                </>
              )}
              {!orgInfo.centre_name && orgInfo.address && (
                <span className="text-wantok-700"> · {orgInfo.address}</span>
              )}
            </div>
          )}
          {orgLookup === 'notfound' && (
            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">
              That code isn't recognised. Double-check with your employer
              or leave the field blank to register without an organisation.
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function Step3({ form, set }) {
  const strength = useMemo(() => passwordStrength(form.password), [form.password])
  return (
    <section className="space-y-4">
      <SectionHeader
        eyebrow="Step 3 of 3"
        title="Set your password"
        sub="At least 8 characters. Mix upper / lower / numbers / symbols for the strongest score."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Username (optional)" hint="Auto-generated from your name if left blank.">
          <Input
            value={form.username} onChange={set('username')}
            placeholder="Leave blank to auto-generate" autoComplete="username"
          />
        </Field>
        <div></div>
        <Field label="Password * (min 8 characters)">
          <Input
            type="password" value={form.password} onChange={set('password')}
            autoComplete="new-password" minLength={8}
          />
          {form.password && <StrengthBar score={strength.score} label={strength.label} />}
        </Field>
        <Field label="Confirm password *">
          <Input
            type="password" value={form.confirm} onChange={set('confirm')}
            autoComplete="new-password" minLength={8}
          />
        </Field>
      </div>

      <div className="border-t border-slate-100 pt-4 space-y-2">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-wantok-700">
          Required consents
        </div>
        <Checkbox
          checked={form.terms_of_service} onChange={set('terms_of_service')}
          label="I accept the Terms of Service and Privacy Policy."
          required
        />
        <Checkbox
          checked={form.telehealth_treatment} onChange={set('telehealth_treatment')}
          label="I consent to receive telehealth medical care via the Wantok platform."
          required
        />
        <Checkbox
          checked={form.data_sharing_with_employer} onChange={set('data_sharing_with_employer')}
          label="(Optional) Share anonymous workforce-health metrics with my employer's HR / wellness lead. Individual records are never shared."
        />
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Bits & pieces
// ---------------------------------------------------------------------------

function Stepper({ step }) {
  const stepLabels = ['Identity', 'Contact & org', 'Account & consent']
  return (
    <ol className="flex items-center gap-2">
      {stepLabels.map((label, i) => {
        const n = i + 1
        const active = n === step
        const done = n < step
        return (
          <li key={n} className="flex items-center gap-2">
            <span className={
              `inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold border ` +
              (done ? 'bg-emerald-500 text-white border-emerald-500'
                : active ? 'bg-wantok-700 text-white border-wantok-700'
                : 'bg-white text-slate-500 border-slate-300')
            }>
              {done ? '✓' : n}
            </span>
            <span className={
              `text-[11px] uppercase tracking-[0.16em] font-bold ` +
              (active ? 'text-wantok-800' : done ? 'text-slate-700' : 'text-slate-400')
            }>{label}</span>
            {n < stepLabels.length && (
              <span className={`mx-1 h-px w-8 ${done ? 'bg-emerald-300' : 'bg-slate-200'}`} />
            )}
          </li>
        )
      })}
    </ol>
  )
}

function SectionHeader({ eyebrow, title, sub }) {
  return (
    <header>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-wantok-700">{eyebrow}</div>
      <h2 className="text-xl font-display font-semibold text-slate-900 mt-0.5">{title}</h2>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </header>
  )
}

function Field({ label, hint, children }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span className="font-semibold text-slate-700">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-slate-400 font-normal">{hint}</span>}
    </label>
  )
}

function Input(props) {
  return <input {...props}
    className={`border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400 ${props.className || ''}`}
  />
}

function Select({ children, ...rest }) {
  return (
    <select {...rest}
      className="border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400">
      {children}
    </select>
  )
}

function Checkbox({ checked, onChange, label, required }) {
  return (
    <label className="flex items-start gap-2 text-sm text-slate-700 leading-snug">
      <input type="checkbox" checked={checked} onChange={onChange}
             className="mt-0.5 w-4 h-4 accent-wantok-700" />
      <span>
        {label}
        {required && <span className="text-coral-500 ml-1">*</span>}
      </span>
    </label>
  )
}

function StrengthBar({ score, label }) {
  // score: 0..4
  const widths = ['10%', '25%', '50%', '75%', '100%']
  const colors = ['bg-rose-400', 'bg-rose-300', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-600']
  return (
    <div className="mt-1">
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-300 ${colors[score]}`}
             style={{ width: widths[score] }} />
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{label}</div>
    </div>
  )
}

// ---- Password strength heuristic ------------------------------------------
function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '' }
  let score = 0
  if (pw.length >= 8)  score++
  if (pw.length >= 12) score++
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++
  if (/\d/.test(pw))   score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  // Cap at 4 (5-bucket bar 0..4)
  score = Math.min(score, 4)
  const labels = ['Too short', 'Weak', 'Fair', 'Strong', 'Very strong']
  return { score, label: labels[score] }
}
